import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Unlock, Loader2, DollarSign, CreditCard, Heart, CheckCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedGoalPage() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [paying, setPaying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchGoal();
  }, [shareToken]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success') {
      setPaymentStatus('success');
    }
  }, [searchParams]);

  const fetchGoal = async () => {
    try {
      const res = await axios.get(`${API}/api/shared/${shareToken}`);
      setGoal(res.data);
    } catch {
      setGoal(null);
    } finally {
      setLoading(false);
    }
  };

  const handleContribute = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/shared/${shareToken}/contribute`, {
        amount: parseFloat(amount),
        contributor_name: name || 'Anonymous',
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vault flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#9333EA]" />
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="min-h-screen bg-vault flex items-center justify-center">
        <div className="text-center">
          <Lock className="h-16 w-16 text-[#A19DB0] mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-white mb-2">Goal Not Found</h2>
          <p className="text-[#A19DB0]">This shared link may be expired or invalid.</p>
        </div>
      </div>
    );
  }

  const progress = goal.target_amount > 0 ? (goal.current_amount / goal.target_amount) * 100 : 0;
  const isUnlocked = new Date(goal.unlock_date) <= new Date();

  return (
    <div className="min-h-screen bg-vault flex items-center justify-center p-4">
      <div className="glass-modal rounded-lg max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="p-8 bg-gradient-to-br from-[#9333EA]/20 to-transparent border-b border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-md bg-[#9333EA] flex items-center justify-center">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-sm font-bold text-[#9333EA]">FutureVault</span>
              <p className="text-xs text-[#A19DB0]">Shared Savings Goal</p>
            </div>
          </div>
          
          <h1 data-testid="shared-goal-name" className="font-heading text-3xl font-black text-white tracking-tight mb-1">
            {goal.name}
          </h1>
          <p className="text-[#A19DB0] flex items-center gap-2">
            <Users className="h-4 w-4" />
            by {goal.owner_name}
          </p>
        </div>

        {/* Progress */}
        <div className="p-8">
          {paymentStatus === 'success' && (
            <div data-testid="contribution-success" className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-md flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
              <p className="text-emerald-400 font-medium">Thank you for your contribution!</p>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <span data-testid="shared-goal-amount" className="font-mono text-4xl font-bold text-white">
                ${goal.current_amount.toFixed(2)}
              </span>
              <span className="text-[#A19DB0]">of ${goal.target_amount.toFixed(2)}</span>
            </div>
            <Progress value={progress} className="h-3 bg-[#12121A]" />
            <div className="flex justify-between mt-2">
              <span className="text-sm text-[#A19DB0]">{progress.toFixed(1)}% funded</span>
              <span className="text-sm text-[#A19DB0]">{goal.deposits_count} deposits</span>
            </div>
          </div>

          {goal.description && (
            <p className="text-[#A19DB0] mb-6">{goal.description}</p>
          )}

          <div className="p-4 bg-[#12121A] rounded-md mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0] mb-1">
              {isUnlocked ? 'Status' : 'Unlocks On'}
            </div>
            <div className="font-mono text-white flex items-center gap-2">
              {isUnlocked ? (
                <><Unlock className="h-4 w-4 text-emerald-400" /> Unlocked</>
              ) : (
                <><Lock className="h-4 w-4 text-[#9333EA]" /> {new Date(goal.unlock_date).toLocaleDateString()}</>
              )}
            </div>
          </div>

          {/* Contribute */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <Heart className="h-5 w-5 text-[#9333EA]" />
              Contribute to this Goal
            </h3>
            
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">Your Name</Label>
                <Input
                  data-testid="contributor-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-dark mt-1"
                  placeholder="Anonymous"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">Amount</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A19DB0]" />
                  <Input
                    data-testid="contribution-amount-input"
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-10 input-dark"
                    placeholder="10.00"
                  />
                </div>
              </div>
              <Button
                data-testid="contribute-button"
                onClick={handleContribute}
                disabled={paying}
                className="w-full btn-primary h-12"
              >
                {paying ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Contribute with Card
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
